<template>
	<div class="bg-accented py-12 sm:py-16 w-full min-w-screen max-w-screen relative isolate overflow-hidden">
		<div>
			<h2 class="pb-4 text-5xl flex flex-col tracking-tight text-center">
				<span class="text-primary font-semibold">
					{{ $t('blog-main.h2.title') }}
				</span>
				<span class="text-highlighted font-medium">
					{{ $t('blog-main.h2.title-2') }}
				</span>
			</h2>
			<div class="max-w-[1600px] mx-auto">
				<UCarousel
					v-if="hasPosts"
					ref="carousel"
					v-slot="{ item: post, index }"
					:autoplay="{ delay: 4000 }"
					:items="posts"
					loop
					:ui="{
						item: 'basis-1/3 md:basis-1/5 group min-w-[75%] w-full sm:w-auto sm:min-w-[350px] md:min-w-[350px] lg:min-w-[400px]'
					}"
					arrows
					:prev="{ color: 'primary', variant: 'soft' }"
					:next="{ color: 'primary', variant: 'soft' }"
					:in-view-threshold="0.5"
					class-names
					wheel-gestures
					class="h-[528px] sm:h-[552px] my-4 pt-2 sm:px-1 w-full overflow-hidden sm:mask-linear-[90deg,transparent_5%,black,transparent_95%]"
				>
					<div class="py-12 sm:py-6">
						<UBlogPost
							v-bind="post"
							:id="BLOG_CARD_ID"
							:key="index"
							orientation="vertical"
							:to="index == selectedSlideIndex ? post.to : ''"
							:image="{
								src: `${post.image}/m/324x162`,
								srcset: `${post.image}/m/220x110 324w, ${post.image}/m/250x125 500w, ${post.image}/m/320x160 800w, ${post.image}/m/420x210 1000w`,
								loading: 'lazy'
							}"
							:ui="{
								date: 'order-last'
							}"
							:class="[
								// Base styles
								'flex flex-col items-center -ml-3 sm:ml-0 justify-center transform transition-all duration-300 delay-150 ease-in-out cursor-pointer',

								// Default for .is-in-view (items further away)
								// This is the base for visible items and will be overridden by more specific states below.
								'group:opacity-8 group:mb-12',

								// Styles for the item BEFORE the .is-snapped item
								// (using the '+' CSS sibling combinator selector for 'the element BEFORE me')
								'group-[&:has(+div.is-snapped)]:opacity-45 group-[&:has(+div.is-snapped)]:mb-10 select-none',

								// Styles for the .is-snapped item itself
								'group-[.is-snapped]:bg-white group-[.is-snapped]:pb-7 group-[.is-snapped]:transform group-[.is-snapped]:scale-[105%] group-[.is-snapped]:-mt-2.5 group-[.is-snapped]:border-b-8 group-[.is-snapped]:shadow-md group-[.is-snapped]:border-b-(--ui-primary) group-[.is-snapped]:opacity-100 group-[.is-snapped]:mb-0',

								// Styles for the item AFTER the .is-snapped item - applied by the is-snapped item to its next sibling
								// (using the '+&' selector since there is no CSS selector for 'the element after me')
								'group-[.is-snapped+&]:opacity-45 group-[.is-snapped+&]:mb-10 select-none'
							]"
							@mouseenter="trackEvent($event, trackingLocation, { title: post.title })"
							@focus="trackEvent($event, trackingLocation, { title: post.title })"
							@click="(e) => {
								trackEvent(e, trackingLocation, { title: post.title });
								if (index !== selectedSlideIndex) { // don't route when clicking a non-active slide, only move to that slide.
									e.preventDefault();
									e.stopPropagation();
									cardClicked(index)
								}
							}"
						>
							<template #date>
								<div class="text-muted mt-auto">
									<NuxtTime
										:datetime="new Date(post.date || '')"
										month="short"
										day="numeric"
										year="numeric"
									/>
								</div>
							</template>
						</UBlogPost>
					</div>
				</UCarousel>
			</div>
			<div class="flex justify-center mt-4 mx-4">
				<UButton
					:id="READ_MORE_ARTICLES_BUTTON_ID"
					:label="$t('blog-main.read-more-articles')"
					:to="localePath('/blog')"
					size="xl"
					:ui="{
						base: 'block sm:inline-flex text-center justify-center'
					}"
					@click="trackEvent($event, trackingLocation)"
					@mouseenter="trackEvent($event, trackingLocation)"
					@focus="trackEvent($event, trackingLocation)"
				/>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useErrorReporter } from '@/composables/useErrorReporter'
import { useGA4Tracking } from '@/composables/useGA4Tracking'
import {
	BLOG_CARD_ID,
	READ_MORE_ARTICLES_BUTTON_ID
} from '@/constants/ga4/tracking-ids'
import type { BlogPostProps } from '@nuxt/ui-pro'
import { useI18n } from 'vue-i18n'

const { bugsnagReport } = useErrorReporter()
const { trackEvent } = useGA4Tracking()

const { locale } = useI18n({
	useScope: 'global'
})
const localePath = useLocalePath()

const props = defineProps({
	id: {
		type: String,
		required: true
	},
	trackingLocation: {
		type: String,
		required: true
	}
})

const {
	id, trackingLocation
} = toRefs(props)

const blogStories = useState<any[]>(`${id.value}-blog-carousel-${locale.value}`, () => [])

// If no blogStories found in SSG payload,
// then fetch via server API during SSR to avoid client-side Storyblok calls
if (!blogStories.value?.length) {
	const { data, error } = await useFetch('/api/blog-articles', {
		params: { language: locale.value }
	})
	if (error.value) {
		bugsnagReport({
			error: new Error('API Error - No blog articles found'),
			showToast: true
		})
	}
	blogStories.value = data.value || []
	if (!blogStories.value?.length) {
		bugsnagReport({
			error: new Error('No blog articles found in Storyblok'),
			showToast: true
		})
	}
}

const posts = computed<BlogPostProps[]>(() => {
	return blogStories.value?.map((story: any): BlogPostProps => {
		const {
			image, title, dateTime, cardBody: preview
		} = story?.content || {}
		return {
			title: title || '',
			description: preview || '',
			date: dateTime || '',
			image: image.filename || '',
			to: `/${story.full_slug}`
		}
	})
})

const hasPosts = computed(() => {
	return !!posts.value?.length
})

const selectedSlideIndex = ref(0)

const handleScroll = () => {
	selectedSlideIndex.value = (carousel.value?.emblaApi?.slidesInView()[0] ?? 0)
}

const carousel = useTemplateRef('carousel')

const cardClicked = (index: number) => {
	carousel.value?.emblaApi?.scrollTo(index)
	setTimeout(() => {
		selectedSlideIndex.value = index
	}, 200)
}

onMounted(() => {
	if (carousel.value?.emblaApi) {
		carousel.value.emblaApi.on('slidesInView', handleScroll)
	}
})
</script>
